"use client";

import { type CourseType, type EntityType } from "@golf-district/shared";
import type { Path } from "~/hooks/usePreviousPath";
import { usePreviousPath } from "~/hooks/usePreviousPath";
import { api } from "~/utils/api";
import { usePathname, useSearchParams } from "next/navigation";
import { LoadingContainer } from "~/app/[course]/loader";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

interface AppContextType {
  prevPath: Path | null;
  entity: EntityType | undefined;
  courses: CourseType[] | undefined;
  setPrevPath: Dispatch<SetStateAction<Path | null>>;
  alertOffersShown: boolean;
  setAlertOffersShown: Dispatch<SetStateAction<boolean>>;
  setIsNavExpanded: Dispatch<SetStateAction<boolean>>;
  isNavExpanded: boolean;
  activePage: string;
  setActivePage: Dispatch<SetStateAction<string>>
  headerHeight: number,
  setHeaderHeight: Dispatch<SetStateAction<number>>
  mainHeaderHeight: number,
  setmainHeaderHeight: Dispatch<SetStateAction<number>>
  showWhiteLabelFooterLogo: boolean;
  setShowWhiteLabelFooterLogo: Dispatch<SetStateAction<boolean>>;
  isNavigating: boolean;
  setIsNavigating: Dispatch<SetStateAction<boolean>>;
  // Records the id of the last-clicked NavItem so two nav links that
  // intentionally share the same destination URL (e.g. "Sell" and "My Tee
  // Box" both landing on /my-tee-box?section=owned) can tell which one the
  // user actually clicked, since the URL alone can't distinguish them.
  lastClickedNavId: string | null;
  setLastClickedNavId: Dispatch<SetStateAction<string | null>>;
}

const AppContext = createContext<AppContextType>({
  prevPath: null,
  entity: undefined,
  courses: undefined,
  setPrevPath: () => undefined,
  alertOffersShown: false,
  setAlertOffersShown: () => ({}),
  isNavExpanded: false,
  setIsNavExpanded: () => ({}),
  activePage: "",
  setActivePage: () => ({}),
  headerHeight: 0,
  setHeaderHeight: () => ({}),
  mainHeaderHeight: 0,
  setmainHeaderHeight: () => ({}),
  showWhiteLabelFooterLogo: true,
  setShowWhiteLabelFooterLogo: () => ({}),
  isNavigating: false,
  setIsNavigating: () => ({}),
  lastClickedNavId: null,
  setLastClickedNavId: () => ({}),
});

export const AppWrapper = ({
  children,
  entityData,
}: {
  children: ReactNode;
  entityData: EntityType | undefined;
}) => {
  const { prevPath, setPrevPath } = usePreviousPath();
  const [entity, setEntity] = useState<EntityType | undefined>(entityData);
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [activePage, setActivePage] = useState<string>("");
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [mainHeaderHeight, setmainHeaderHeight] = useState<number>(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [lastClickedNavId, setLastClickedNavId] = useState<string | null>(null);
  const pathname = usePathname();
  // Query-only transitions (e.g. switching My Tee Box's owned/offers/etc.
  // section tabs) leave pathname unchanged, so clearing on pathname alone
  // missed them — a subsequent nav-bar click landing back on the same path
  // (different section) would set isNavigating with nothing left to clear
  // it, stuck until the 20s safety net below. usePreviousPath() above
  // already calls useSearchParams() unguarded, so the app is already
  // dynamically rendered from here down — no additional Suspense needed.
  const searchParamsString = useSearchParams().toString();

  useEffect(() => {
    setIsNavigating(false);
  }, [pathname, searchParamsString]);

  useEffect(() => {
    if (!isNavigating) return;
    const timeout = setTimeout(() => setIsNavigating(false), 20000);
    return () => clearTimeout(timeout);
  }, [isNavigating]);

  useEffect(() => {
    if (entityData) {
      setEntity(entityData);
    }
  }, [entityData]);

  const entityId = entity?.id;

  const { data: courses } = api.entity.getCoursesByEntityId.useQuery(
    { entityId: entityId! },
    {
      enabled: entityId !== undefined,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );
  const [alertOffersShown, setAlertOffersShown] = useState<boolean>(false);
  const [showWhiteLabelFooterLogo, setShowWhiteLabelFooterLogo] = useState<boolean>(true);
  const settings = {
    prevPath,
    entity,
    courses,
    setPrevPath,
    alertOffersShown,
    setAlertOffersShown,
    isNavExpanded,
    setIsNavExpanded,
    activePage,
    setActivePage,
    headerHeight,
    setHeaderHeight,
    mainHeaderHeight,
    setmainHeaderHeight,
    showWhiteLabelFooterLogo,
    setShowWhiteLabelFooterLogo,
    isNavigating,
    setIsNavigating,
    lastClickedNavId,
    setLastClickedNavId,
  };

  return (
    <AppContext.Provider value={settings}>
      <LoadingContainer isLoading={isNavigating}>
        <div></div>
      </LoadingContainer>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(AppContext);
};
